import { useState } from 'react';
import { FileUpload } from '../components/FileUpload';
import { CameraCapture } from '../components/CameraCapture';
import { api } from '../services/api';
import type { DocumentType, UploadResponse, ExtractedItem } from '../types';

type TabType = 'upload' | 'camera';

export function UploadPage() {
  const [activeTab, setActiveTab] = useState<TabType>('upload');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File, documentType: DocumentType) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.uploadDocument(file, documentType);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCapture = async (blob: Blob, documentType: DocumentType) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.uploadDocument(blob, documentType, 'camera_capture.jpg');
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setIsLoading(false);
    }
  };

  const clearResult = () => {
    setResult(null);
    setError(null);
  };

  return (
    <div className="upload-page">
      <h1>Upload Document</h1>
      <p className="page-description">
        Upload invoices, receipts, or take photos of inventory items
      </p>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {result ? (
        <ResultView result={result} onClear={clearResult} />
      ) : (
        <>
          <div className="tab-switcher">
            <button
              className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
              onClick={() => setActiveTab('upload')}
            >
              📄 File Upload
            </button>
            <button
              className={`tab ${activeTab === 'camera' ? 'active' : ''}`}
              onClick={() => setActiveTab('camera')}
            >
              📷 Camera
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'upload' ? (
              <FileUpload onUpload={handleUpload} isLoading={isLoading} />
            ) : (
              <CameraCapture onCapture={handleCapture} isLoading={isLoading} />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ResultView({ result, onClear }: { result: UploadResponse; onClear: () => void }) {
  return (
    <div className="result-view">
      <div className="result-header">
        <h2>Processing Complete</h2>
        <span className="processing-time">
          Processed in {(result.processingTimeMs / 1000).toFixed(1)}s
        </span>
      </div>

      {result.items.length > 0 && (
        <div className="extracted-items">
          <h3>Extracted Items ({result.items.length})</h3>
          <div className="items-table">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>Price</th>
                  <th>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((item: ExtractedItem, index: number) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>{item.unit}</td>
                    <td>{item.totalPrice ? `$${item.totalPrice.toFixed(2)}` : '-'}</td>
                    <td>
                      <span className={`confidence ${getConfidenceClass(item.confidence)}`}>
                        {Math.round(item.confidence * 100)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result.rawText && (
        <div className="raw-text">
          <h3>Raw Text</h3>
          <pre>{result.rawText}</pre>
        </div>
      )}

      <button className="clear-btn" onClick={onClear}>
        Upload Another Document
      </button>
    </div>
  );
}

function getConfidenceClass(confidence: number): string {
  if (confidence >= 0.9) return 'high';
  if (confidence >= 0.7) return 'medium';
  return 'low';
}
